import type { TaxCalculationStrategy, CountryConfig } from '../types'

export const usaTaxStrategy: TaxCalculationStrategy = {
  calculateIncomeTax(income: number, config: CountryConfig): number {
    if (!config.taxRules.hasIncomeTax || income <= 0) return 0

    let tax = 0
    let remainingIncome = income

    for (const bracket of config.taxRules.brackets) {
      if (remainingIncome <= 0) break

      const bracketMax = bracket.max ?? Infinity
      const taxableInBracket = Math.min(
        remainingIncome,
        bracketMax - bracket.min
      )

      if (taxableInBracket > 0) {
        tax += taxableInBracket * bracket.rate
        remainingIncome -= taxableInBracket
      }
    }

    return Math.round(tax)
  },

  calculateCapitalGainsTax(
    gains: number,
    holdingDays: number,
    config: CountryConfig
  ): number {
    if (gains <= 0) return 0

    const isLongTerm = holdingDays >= config.taxRules.ltcgThresholdDays
    const rate = isLongTerm ? config.taxRules.ltcgRate : config.taxRules.stcgRate

    return Math.round(gains * rate)
  },

  calculateWithdrawalTax(
    amount: number,
    assetId: string,
    age: number,
    config: CountryConfig
  ): number {
    if (amount <= 0) return 0

    // Find the asset definition
    let assetDef = null
    for (const category of Object.values(config.assets)) {
      const found = category.find((a: { id: string }) => a.id === assetId)
      if (found) {
        assetDef = found
        break
      }
    }

    if (!assetDef || assetDef.withdrawalRules.length === 0) {
      return 0
    }

    // Find applicable withdrawal rule based on age
    const applicableRule = assetDef.withdrawalRules.find((rule: { minAge: number; maxAge?: number }) => {
      const meetsMin = age >= rule.minAge
      const meetsMax = rule.maxAge === undefined || age < rule.maxAge
      return meetsMin && meetsMax
    })

    if (!applicableRule) {
      // Default to first rule if no age match
      return Math.round(amount * assetDef.withdrawalRules[0].penaltyRate)
    }

    return Math.round(amount * applicableRule.penaltyRate)
  },
}
